import React from "react";
import { isValid } from "date-fns";
import { buildTimeFromParts, getTimeParts } from "./timePickerUtils.js";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const selectStyle = {
  flex: 1,
  minWidth: 0,
  height: 36,
  padding: "0 8px",
  borderRadius: "var(--radius-xs)",
  border: "none",
  boxShadow: "inset 0 0 0 1px var(--border)",
  background: "var(--white)",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: 14,
  color: "var(--text-body)",
  cursor: "pointer",
};

const periodBtnStyle = (active) => ({
  flex: 1,
  height: 36,
  border: "none",
  borderRadius: "var(--radius-xs)",
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  background: active ? "var(--mint-light)" : "var(--white)",
  color: active ? "var(--text-strong)" : "var(--text-muted)",
  boxShadow: active ? "inset 0 0 0 1.5px var(--green-bright)" : "inset 0 0 0 1px var(--border)",
});

/** Scrollable hour/minute/period selectors for TimePicker popover. */
export function TimePanel({ value, onSelect, disabled = false }) {
  const parts = React.useMemo(() => getTimeParts(value && isValid(value) ? value : null), [value]);

  const emit = (nextParts) => {
    if (disabled || !onSelect) return;
    onSelect(buildTimeFromParts(nextParts.hour12, nextParts.minute, nextParts.period));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 220 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select
          aria-label="Hour"
          disabled={disabled}
          value={parts.hour12}
          onChange={(e) => emit({ ...parts, hour12: Number(e.target.value) })}
          style={selectStyle}
        >
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, "0")}
            </option>
          ))}
        </select>
        <span style={{ fontWeight: 600, color: "var(--text-muted)" }}>:</span>
        <select
          aria-label="Minute"
          disabled={disabled}
          value={parts.minute}
          onChange={(e) => emit({ ...parts, minute: Number(e.target.value) })}
          style={selectStyle}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, "0")}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: "flex", gap: 8 }} role="group" aria-label="AM or PM">
        {["AM", "PM"].map((period) => (
          <button
            key={period}
            type="button"
            disabled={disabled}
            aria-pressed={parts.period === period}
            onClick={() => emit({ ...parts, period })}
            style={periodBtnStyle(parts.period === period)}
          >
            {period}
          </button>
        ))}
      </div>
    </div>
  );
}

export default TimePanel;
